import {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import type {ReactNode} from 'react';
import type {BrandAppData} from '../../shared/brand-api';
import {BrandApiError, describe, fetchBrand, signOut as signOutRequest} from './api';

type Status = 'loading' | 'ready' | 'signed-out' | 'unavailable';

interface BrandStore {
  status: Status;
  data: BrandAppData | null;
  failure: string;
  notice: {text: string; error: boolean};
  toast(text: string, error?: boolean): void;
  reload(silent?: boolean): Promise<void>;
  enter(): Promise<void>;
  signOut(): Promise<void>;
}

const Context = createContext<BrandStore | null>(null);

export function BrandProvider({children}: {children: ReactNode}) {
  const [status, setStatus] = useState<Status>('loading');
  const [data, setData] = useState<BrandAppData | null>(null);
  const [failure, setFailure] = useState('');
  const [notice, setNotice] = useState({text: '', error: false});
  const timer = useRef<number | null>(null);
  const toast = useCallback((text: string, error = false) => {
    setNotice({text, error});
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setNotice({text: '', error: false}), 3600);
  }, []);
  const reload = useCallback(async (silent = true) => {
    try {
      const next = await fetchBrand();
      setData(next); setStatus('ready'); setFailure('');
    } catch (error) {
      if (error instanceof BrandApiError && error.status === 401) { setData(null); setStatus('signed-out'); return; }
      if (silent) toast(describe(error), true);
      else { setStatus('unavailable'); setFailure(describe(error)); }
    }
  }, [toast]);
  useEffect(() => { void reload(false); }, [reload]);
  useEffect(() => {
    const id = window.setInterval(() => { if (!document.hidden && status === 'ready') void reload(true); }, 45_000);
    return () => window.clearInterval(id);
  }, [reload, status]);
  const enter = useCallback(async () => { setStatus('loading'); await reload(false); }, [reload]);
  const signOut = useCallback(async () => {
    try { await signOutRequest(); } catch { /* the local state still closes */ }
    setData(null); setStatus('signed-out');
  }, []);
  const value = useMemo(() => ({status, data, failure, notice, toast, reload, enter, signOut}),
    [status, data, failure, notice, toast, reload, enter, signOut]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useBrand(): BrandStore {
  const value = useContext(Context);
  if (!value) throw new Error('useBrand outside provider');
  return value;
}
